import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Loader2, CheckCircle, AlertCircle, RefreshCw, Send, MessageSquare, Mail, MessageSquareText, Phone, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { channelService } from '../services/ChannelService';
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';
import OrgWhatsAppPanel from '../components/features/settings/OrgWhatsAppPanel';
import { useAuth } from '../contexts/AuthContext';

// Channel configuration
interface ChannelConfig {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  gradientFrom: string;
  gradientTo: string;
  requiresQR: boolean;
  comingSoon?: boolean;
}

const AVAILABLE_CHANNELS: ChannelConfig[] = [
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    description: 'Connect your WhatsApp to manage customer inquiries',
    icon: MessageCircle,
    color: 'green',
    gradientFrom: 'from-green-400',
    gradientTo: 'to-green-600',
    requiresQR: true,
  },
  {
    id: 'email',
    name: 'Email',
    description: 'Connect your email to manage customer inquiries',
    icon: Mail,
    color: 'blue',
    gradientFrom: 'from-blue-400',
    gradientTo: 'to-blue-600',
    requiresQR: false,
    comingSoon: true,
  },
  {
    id: 'sms',
    name: 'SMS',
    description: 'Connect SMS to manage customer inquiries',
    icon: MessageSquareText,
    color: 'purple',
    gradientFrom: 'from-purple-400',
    gradientTo: 'to-purple-600',
    requiresQR: false,
    comingSoon: true,
  },
  {
    id: 'phone',
    name: 'Phone',
    description: 'Connect phone calls to manage customer inquiries',
    icon: Phone,
    color: 'indigo',
    gradientFrom: 'from-indigo-400',
    gradientTo: 'to-indigo-600',
    requiresQR: false,
    comingSoon: true,
  },
  // Add more channels as needed
  // {
  //   id: 'telegram',
  //   name: 'Telegram',
  //   description: 'Connect your Telegram to manage customer inquiries',
  //   icon: Send,
  //   color: 'blue',
  //   gradientFrom: 'from-blue-400',
  //   gradientTo: 'to-blue-600',
  //   requiresQR: false,
  // },
];

interface ChannelSettingsPanelProps {
  channel: ChannelConfig;
  settings: any;
  loading: boolean;
  error: string | null;
  onUpdateSettings: (updates: any) => Promise<void>;
}

const ChannelSettingsPanel: React.FC<ChannelSettingsPanelProps> = ({
  channel,
  settings,
  loading,
  error,
  onUpdateSettings,
}) => {
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [defaultResponse, setDefaultResponse] = useState('');
  const [responseDelay, setResponseDelay] = useState('0');
  const [workingHoursEnabled, setWorkingHoursEnabled] = useState(false);
  const [workingHoursStart, setWorkingHoursStart] = useState('09:00');
  const [workingHoursEnd, setWorkingHoursEnd] = useState('17:00');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.settings) {
      setAutoReplyEnabled(settings.settings.auto_reply_enabled || false);
      setWelcomeMessage(settings.settings.welcome_message || '');
      setDefaultResponse(settings.settings.default_response || '');
      setResponseDelay(settings.settings.response_delay?.toString() || '0');
      setWorkingHoursEnabled(settings.settings.working_hours_enabled || false);
      setWorkingHoursStart(settings.settings.working_hours_start || '09:00');
      setWorkingHoursEnd(settings.settings.working_hours_end || '17:00');
    }
  }, [settings]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await onUpdateSettings({
        enabled: settings?.enabled ?? true,
        settings: {
          auto_reply_enabled: autoReplyEnabled,
          welcome_message: welcomeMessage,
          default_response: defaultResponse,
          response_delay: parseInt(responseDelay) || 0,
          working_hours_enabled: workingHoursEnabled,
          working_hours_start: workingHoursStart,
          working_hours_end: workingHoursEnd,
        },
      });
    } catch (err) {
      // Error is handled by parent
    } finally {
      setSaving(false);
    }
  };

  if (loading && !settings) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
        <h4 className="text-sm font-medium text-gray-900">Message Settings</h4>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="px-3 py-1.5 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-400 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
        >
          {saving ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving...
            </>
          ) : (
            'Save changes'
          )}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 rounded-lg border border-red-100">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Auto Reply Toggle */}
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
        <div>
          <p className="text-sm font-medium text-gray-900">Auto-reply</p>
          <p className="text-xs text-gray-500 mt-0.5">Automatically respond to messages</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={autoReplyEnabled}
            onChange={(e) => setAutoReplyEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-900"></div>
        </label>
      </div>

      {/* Welcome Message */}
      <div className="p-3 bg-white rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Welcome message
        </label>
        <textarea
          value={welcomeMessage}
          onChange={(e) => setWelcomeMessage(e.target.value)}
          placeholder="Enter a welcome message..."
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none"
        />
        <p className="text-xs text-gray-500 mt-1.5">Sent when starting a new conversation</p>
      </div>

      {/* Default Response */}
      <div className="p-3 bg-white rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Default response
        </label>
        <textarea
          value={defaultResponse}
          onChange={(e) => setDefaultResponse(e.target.value)}
          placeholder="Enter a default response..."
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none"
        />
        <p className="text-xs text-gray-500 mt-1.5">Used when auto-reply is enabled</p>
      </div>

      {/* Response Delay */}
      <div className="p-3 bg-white rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Response delay (seconds)
        </label>
        <input
          type="number"
          min="0"
          max="60"
          value={responseDelay}
          onChange={(e) => setResponseDelay(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
        />
        <p className="text-xs text-gray-500 mt-1.5">Delay before sending auto-replies</p>
      </div>

      {/* Working Hours */}
      <div className="p-3 bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-medium text-gray-900">Working hours</p>
            <p className="text-xs text-gray-500 mt-0.5">Auto-reply during business hours only</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={workingHoursEnabled}
              onChange={(e) => setWorkingHoursEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-900"></div>
          </label>
        </div>

        {workingHoursEnabled && (
          <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-200">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Start time</label>
              <input
                type="time"
                value={workingHoursStart}
                onChange={(e) => setWorkingHoursStart(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">End time</label>
              <input
                type="time"
                value={workingHoursEnd}
                onChange={(e) => setWorkingHoursEnd(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
            </div>
          </div>
        )}
      </div>

      {/* Channel Enabled Toggle */}
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
        <div>
          <p className="text-sm font-medium text-gray-900">Channel enabled</p>
          <p className="text-xs text-gray-500 mt-0.5">Enable or disable this channel</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={settings?.enabled ?? true}
            onChange={async (e) => {
              try {
                await onUpdateSettings({
                  enabled: e.target.checked,
                  settings: settings?.settings,
                });
              } catch (err) {
                // Error handled by parent
              }
            }}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-900"></div>
        </label>
      </div>
    </div>
  );
};

const SalesChannelsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isOrgAdmin = currentUser?.role === 'admin' || currentUser?.role === 'org_admin';
  const [selectedChannel, setSelectedChannel] = useState<ChannelConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');
  const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [channelSettings, setChannelSettings] = useState<any>(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Check status on mount and set up polling
  useEffect(() => {
    if (!selectedChannel) return;

    const initializeConnection = async () => {
      await checkStatus();

      // If not connected after status check and channel requires QR, automatically generate QR code
      if (connectionStatus === 'disconnected' && !qrCodeUrl && selectedChannel.requiresQR) {
        handleChannelLogin();
      }
    };

    initializeConnection();

    // Poll status every 5 seconds when QR code is displayed OR when already connected
    if (qrCodeUrl || connectionStatus === 'connected') {
      const interval = setInterval(checkStatus, 8000);
      setStatusCheckInterval(interval);
      return () => clearInterval(interval);
    }
  }, [qrCodeUrl, connectionStatus, selectedChannel]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [statusCheckInterval]);

  const checkStatus = async () => {
    if (!selectedChannel) return;

    try {
      const status = await channelService.getChannelStatus(selectedChannel.id);
      if (status.is_connected && status.is_logged_in) {
        setConnectionStatus('connected');
        setQrCodeUrl(null); // Clear QR code when connected
        // Store connection status in localStorage
        localStorage.setItem(`${selectedChannel.id}_connected`, 'true');
        if (statusCheckInterval) {
          clearInterval(statusCheckInterval);
          setStatusCheckInterval(null);
        }
      } else {
        // Not connected or not logged in
        if (connectionStatus === 'connected') {
          // Was connected, now disconnected
          setConnectionStatus('disconnected');
          localStorage.removeItem(`${selectedChannel.id}_connected`);
        }
      }
    } catch (err) {
      console.error('Error checking status:', err);
      // If error checking status and we thought we were connected, mark as disconnected
      if (connectionStatus === 'connected') {
        setConnectionStatus('disconnected');
        localStorage.removeItem(`${selectedChannel.id}_connected`);
      }
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleChannelLogin = async () => {
    if (!selectedChannel) return;

    setLoading(true);
    setError(null);
    setQrCodeUrl(null);
    setConnectionStatus('connecting');

    try {
      // Get QR code or connection status from backend
      const response = await channelService.loginToChannel(selectedChannel.id);

      // Check if already connected
      if (response.alreadyConnected) {
        setConnectionStatus('connected');
        localStorage.setItem(`${selectedChannel.id}_connected`, 'true');
        setLoading(false);
        return;
      }

      // Create URL from blob
      if (response.blob) {
        const url = URL.createObjectURL(response.blob);
        setQrCodeUrl(url);
      } else {
        throw new Error('No QR code received from server');
      }
    } catch (err: any) {
      console.error(`Error getting ${selectedChannel.name} QR code:`, err);
      setError(err.response?.data?.detail || 'Failed to generate QR code. Please try again.');
      setConnectionStatus('disconnected');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    setQrCodeUrl(null);
    setConnectionStatus('disconnected');
  };

  const handleDisconnect = async () => {
    if (!selectedChannel) return;

    if (!window.confirm(`Are you sure you want to disconnect ${selectedChannel.name}? You will need to scan a QR code again to reconnect.`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await channelService.disconnectChannel(selectedChannel.id);
      setConnectionStatus('disconnected');
      setQrCodeUrl(null);
      localStorage.removeItem(`${selectedChannel.id}_connected`);
    } catch (err: any) {
      console.error(`Error disconnecting ${selectedChannel.name}:`, err);
      setError(err.response?.data?.detail || 'Failed to disconnect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChannel = (channel: ChannelConfig) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedChannel(channel);
      setError(null);
      setQrCodeUrl(null);
      setConnectionStatus('disconnected');
      setIsCheckingStatus(true);
      setShowSettings(false);
      setChannelSettings(null);
      setIsTransitioning(false);
    }, 150);
  };

  const handleBackToChannels = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedChannel(null);
      setError(null);
      setQrCodeUrl(null);
      setConnectionStatus('disconnected');
      setShowSettings(false);
      setChannelSettings(null);
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
        setStatusCheckInterval(null);
      }
      setIsTransitioning(false);
    }, 150);
  };

  const loadChannelSettings = async () => {
    if (!selectedChannel) return;

    setLoadingSettings(true);
    setSettingsError(null);

    try {
      const settings = await channelService.getChannelSettings(selectedChannel.id);
      setChannelSettings(settings);
    } catch (err: any) {
      console.error('Error loading channel settings:', err);
      setSettingsError(err.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleToggleSettings = async () => {
    if (!showSettings && !channelSettings) {
      await loadChannelSettings();
    }
    setShowSettings(!showSettings);
  };

  const handleUpdateSettings = async (updates: any) => {
    if (!selectedChannel) return;

    setLoadingSettings(true);
    setSettingsError(null);

    try {
      const updatedSettings = await channelService.updateChannelSettings(selectedChannel.id, updates);
      setChannelSettings(updatedSettings);
    } catch (err: any) {
      console.error('Error updating channel settings:', err);
      setSettingsError(err.response?.data?.detail || 'Failed to update settings');
      throw err;
    } finally {
      setLoadingSettings(false);
    }
  };

  return (
    <AppLayout pageTitle="Channels">
      <MobileHeader
        title={selectedChannel ? `${selectedChannel.name}` : "Channels"}
        showBack={selectedChannel !== null}
        onBackClick={handleBackToChannels}
      />
      <div className="flex-1 overflow-auto bg-main-bg min-h-full pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {!selectedChannel ? (
            // Channel Selection View
            <div
              className={`space-y-6 transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'
                }`}
            >
              <div className="space-y-2">
                <p className="text-sm sm:text-base text-gray-500">Connect your communication channels</p>
              </div>

              <div className="space-y-2">
                {AVAILABLE_CHANNELS.map((channel) => {
                  const Icon = channel.icon;
                  return (
                    <button
                      key={channel.id}
                      onClick={() => !channel.comingSoon && handleSelectChannel(channel)}
                      disabled={channel.comingSoon}
                      className={`w-full group relative ${channel.comingSoon
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:bg-gray-50 cursor-pointer'
                        }`}
                    >
                      <div className="flex items-center gap-4 p-4 sm:p-5 rounded-xl border border-gray-200 transition-all">
                        <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${channel.comingSoon ? 'bg-gray-100' : `bg-${channel.color}-50`
                          }`}>
                          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${channel.comingSoon ? 'text-gray-400' : `text-${channel.color}-600`
                            }`} />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm sm:text-base font-medium text-gray-900">{channel.name}</span>
                            {channel.comingSoon && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
                                Soon
                              </span>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{channel.description}</p>
                        </div>
                        {!channel.comingSoon && (
                          <ArrowLeft className="w-4 h-4 text-gray-400 rotate-180 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            // Channel Connection View
            <div
              className={`space-y-6 transition-all duration-300 ${isTransitioning ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0'
                }`}
            >
              {/* Channel Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center bg-${selectedChannel.color}-50`}>
                    {React.createElement(selectedChannel.icon, { className: `w-5 h-5 sm:w-6 sm:h-6 text-${selectedChannel.color}-600` })}
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">{selectedChannel.name}</h2>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{selectedChannel.description}</p>
                  </div>
                </div>
                {connectionStatus === 'connected' && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-green-700">Connected</span>
                  </div>
                )}
              </div>

              {/* Org-level WhatsApp panel for admins */}
              {selectedChannel.id === 'whatsapp' && isOrgAdmin ? (
                <OrgWhatsAppPanel />
              ) : (
                <div className="space-y-4">
              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-red-700">{error}</p>
                      <button
                        onClick={handleRetry}
                        className="text-sm text-red-600 hover:text-red-700 font-medium mt-2 inline-flex items-center gap-1"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Try again
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* QR Code Display (if channel requires QR) */}
              {qrCodeUrl && selectedChannel.requiresQR && (
                <div className="space-y-4">
                  <div className="flex flex-col items-center p-8 rounded-xl border border-gray-200">
                    <div className="mb-6">
                      <img
                        src={qrCodeUrl}
                        alt={`${selectedChannel.name} QR Code`}
                        className="w-56 h-56 sm:w-64 sm:h-64 rounded-lg"
                      />
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Scan with {selectedChannel.name} to connect
                    </p>
                    <div className="flex items-center gap-2 text-blue-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm font-medium">Waiting for connection...</span>
                    </div>
                  </div>

                  <button
                    onClick={handleChannelLogin}
                    disabled={loading}
                    className="w-full py-2.5 px-4 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed text-gray-700 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Generate new QR code
                  </button>
                </div>
              )}

              {/* Loading State */}
              {!qrCodeUrl && connectionStatus !== 'connected' && (loading || isCheckingStatus) && (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400 mb-3" />
                  <span className="text-sm text-gray-500">
                    {isCheckingStatus ? 'Checking status...' : 'Generating QR code...'}
                  </span>
                </div>
              )}

              {/* Connected State */}
              {connectionStatus === 'connected' && (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-green-900">Connected</p>
                        <p className="text-sm text-green-700 mt-0.5">
                          Your {selectedChannel.name} is ready to receive messages
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Settings Toggle Button */}
                  <button
                    onClick={handleToggleSettings}
                    className="w-full py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    {showSettings ? 'Hide settings' : 'Configure settings'}
                  </button>

                  {/* Settings Panel */}
                  {showSettings && (
                    <ChannelSettingsPanel
                      channel={selectedChannel}
                      settings={channelSettings}
                      loading={loadingSettings}
                      error={settingsError}
                      onUpdateSettings={handleUpdateSettings}
                    />
                  )}

                  {/* Disconnect Button */}
                  <button
                    onClick={handleDisconnect}
                    disabled={loading}
                    className="w-full py-2.5 px-4 bg-white hover:bg-red-50 disabled:bg-gray-50 disabled:cursor-not-allowed text-red-600 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 border border-gray-200"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Disconnecting...
                      </>
                    ) : (
                      'Disconnect'
                    )}
                  </button>
                </div>
              )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default SalesChannelsPage;
